import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, CalendarOff, Search } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import { refreshSchedulePending } from '../../lib/schedulePending.js'
import { nameInitial } from '../../lib/text.js'
import { doctorCode, doctorFullName } from '../../lib/dentist.js'
import {
  leaveStatusMeta, toneClass, leaveDateLabel, leaveWindowLabel, leaveDayCount,
} from '../../lib/doctorLeave.js'
import LeaveDecisionDrawer from '../../components/schedule/LeaveDecisionDrawer.jsx'

/**
 * การลา (/manager/schedule/doctor/leave) — doctors' leave requests, and the
 * decision.
 *
 * A second-level tab inside ตารางเวร › ทันตแพทย์, beside the roster it collides
 * with — the question a manager has to answer is "what does this hit", and the
 * answer is one tab away. RequireStaff (Manager/GM/HR/Admin) matches the
 * /api/hr/* proxies exactly, so no extra tab gate is needed and HR keeps leave
 * approval.
 *
 * ⚠️ NOT `employee_timetable`. Employee leave (ลากิจ/ลาป่วย against
 * Employee.leavequota) is a different table and a different population — see
 * src/lib/doctorLeave.js.
 *
 * There is no branch filter: a leave row has no branch, because a doctor works
 * at several and is asking to be away from all of them.
 */

const CARD = 'rounded-2xl border border-slate-200 bg-white'
const INPUT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

const STATUS_TABS = [
  ['pending', 'รอยืนยัน'],
  ['approved', 'อนุมัติ'],
  ['rejected', 'ไม่อนุมัติ'],
  ['', 'ทั้งหมด'],
]

export default function DoctorLeave() {
  // Defaults to รอยืนยัน — the only tab that represents work to do.
  const [status, setStatus] = usePersistedState('doctorleave:status', 'pending')
  const [q, setQ] = usePersistedState('doctorleave:q', '')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [openId, setOpenId] = useState(null)
  const [doctors, setDoctors] = useState([])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/hr/doctor-leave', status ? { status } : undefined)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดคำขอลาไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    let alive = true
    cachedGet('/api/doctors', { slim: 1 })
      .then((d) => { if (alive) setDoctors(Array.isArray(d) ? d : (d?.items || [])) })
      .catch(() => {})
    return () => { alive = false }
  }, [])

  // Filtered in memory: the list is one page of a small table, and a round trip
  // per keystroke would be slower and no more capable — the same call
  // /manager/dentist makes for the doctor directory.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    if (!needle) return rows
    return rows.filter((r) => (
      `${r.code || ''} ${r.nickname || ''} ${r.name || ''} ${r.reason || ''}`.toLowerCase().includes(needle)
    ))
  }, [rows, q])

  const pendingCount = rows.filter((r) => r.status === 'pending').length

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1">
          {STATUS_TABS.map(([v, label]) => (
            <button
              key={v || 'all'}
              onClick={() => setStatus(v)}
              className={`rounded-lg px-2.5 py-1.5 text-[13px] transition-colors ${
                status === v ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100' : 'text-slate-500 hover:bg-slate-50'
              }`}
            >
              {label}
              {v === 'pending' && pendingCount > 0 && status === 'pending' && (
                <span className="ml-1 text-[11.5px] opacity-70">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="relative ml-auto">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            className={`${INPUT} pl-8 w-56`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นหาคุณหมอ…"
          />
        </div>
      </div>

      {err && <div className="mb-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-[13px] text-rose-700">{err}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-[13.5px] text-slate-500 py-10 justify-center">
          <Loader2 size={16} className="animate-spin" /> กำลังโหลด…
        </div>
      ) : shown.length === 0 ? (
        <div className={`${CARD} px-6 py-12 text-center`}>
          <CalendarOff size={28} strokeWidth={1.5} className="mx-auto text-slate-300 mb-2" />
          <p className="text-[14px] text-slate-500">
            {status === 'pending' ? 'ไม่มีคำขอลาที่รอยืนยัน' : 'ไม่มีคำขอลา'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {shown.map((r) => <LeaveRow key={r.id} row={r} onOpen={() => setOpenId(r.id)} />)}
        </div>
      )}

      {openId != null && (
        <LeaveDecisionDrawer
          id={openId}
          onClose={() => setOpenId(null)}
          // `fresh` bypasses the proxy's 2-minute shared cache. A manager who
          // approves a leave and watches the sidebar badge sit there learns the
          // badge is lying — and a badge nobody trusts is worse than none.
          onDecided={() => { setOpenId(null); load(); refreshSchedulePending({ fresh: true }) }}
        />
      )}
    </div>
  )
}

function LeaveRow({ row, onOpen }) {
  const meta = leaveStatusMeta(row.status)
  const days = leaveDayCount(row)
  const label = doctorCode(row) || doctorFullName(row) || `หมอ #${row.doctor_id}`
  return (
    <button onClick={onOpen} className={`${CARD} w-full text-left px-4 py-3 hover:border-slate-300 transition-colors`}>
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-brand-50 text-brand-700 ring-1 ring-brand-100 flex items-center justify-center text-[12px] font-semibold shrink-0">
          {nameInitial(row.nickname || row.name || label)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[14px] font-medium text-slate-900 truncate">{label}</span>
            <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11.5px] ring-1 ${toneClass(meta.tone)}`}>
              {meta.label}
            </span>
          </div>
          <p className="text-[13px] text-slate-600 mt-0.5">
            {leaveDateLabel(row)}
            <span className="text-slate-400"> · </span>
            {leaveWindowLabel(row)}
            {row.type === 'full_day' && days > 1 && <span className="text-slate-400"> · {days} วัน</span>}
          </p>
          {row.reason && <p className="text-[12.5px] text-slate-400 mt-0.5 line-clamp-1">{row.reason}</p>}
        </div>
      </div>
    </button>
  )
}
